import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toast } from '@livable/saas-react-components';
import { useEffect, useRef } from 'react';
import { useCurrentSubscriptionTier } from 'src/components/SubscriptionTier';
import { apPushNeedsPlan } from 'src/components/SubscriptionTier/tiers';
import { APPFOLIO_PUSH_STATUS } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { useStore } from 'src/stores';
import { getAppfolioPushRowState } from './appfolioPushRow';
const POLL_INTERVAL_MS = 5000;

/**
 * Caps, matching what the uploaded-bill poller already uses. A pending push can be stuck for good — a
 * Kafka publish that failed leaves a row nothing will ever finish, and recovering it is a re-click — so
 * an unbounded poll would ask forever about a document that is never going to answer.
 */
const MAX_POLLS = 120;
const MAX_FAILED_POLLS = 3;

/**
 * Said once, when failures end the reads (LB-5982): each read is quiet, and a push shown as pending that has
 * stopped being checked would otherwise look stuck. Reopening the bill reads it again.
 */
const STOPPED_UPDATING = {
  title: "This bill's AppFolio push status stopped updating",
  body: ["We couldn't reach the server, so the status shown may be out of date. Reopen the bill to check again."]
};

/**
 * The one bill the details view is showing, read from `appfolio-push-status` rather than from the list.
 * Two reasons it reads for itself:
 *
 * - The list poll deliberately rests while this view is open, so nothing else is watching this bill.
 * - The list row carries the verdict but no attempts, and the blockers behind a failure — the only place
 *   the way out of `partial_invoice` or `amount_mismatch` is stated — live on the attempt.
 *
 * Until the first read lands, the row's own verdict stands in, so the view opens with something true
 * rather than blank.
 */
export const useBillApPushDetails = _ref => {
  let billId = _ref.billId,
    isEnabled = _ref.isEnabled,
    listApPush = _ref.listApPush;
  const store = useStore();
  // The same plan the table reads, so the details offer no push the bill's row doesn't (LB-6188).
  const needsPlan = apPushNeedsPlan(useCurrentSubscriptionTier());
  const apPushOnSetups = !!store.account.featureFlagsStore.getIsBillingOptionsEnabled;
  const pollCountRef = useRef(0);
  const failedPollCountRef = useRef(0);
  const readStatus = billId ? store.appfolioPush.getStatus(billId) : null;
  const hasReadStatus = !!billId && store.appfolioPush.hasReadStatus(billId);
  const rowStatus = listApPush && billId ? _objectSpread(_objectSpread({}, listApPush), {}, {
    billId,
    attempts: []
  }) : null;

  // The read wins once it lands, because it carries the attempts the row cannot. Before that — and
  // again after a trigger drops it — the row's verdict stands in: the same verdict, off the same
  // ledger, just without the detail behind it.
  const pushStatus = readStatus !== null && readStatus !== void 0 ? readStatus : rowStatus;

  /**
   * Arming the poll is part of the effect's identity, not just something it decides once. A view opened
   * on an unpushed document reads once and rests; the push that starts a moment later has to wake it,
   * and it does so by turning this true.
   */
  const isOnItsWay = (pushStatus === null || pushStatus === void 0 ? void 0 : pushStatus.status) === APPFOLIO_PUSH_STATUS.pending;
  useEffect(() => {
    if (!isEnabled || !billId) return undefined;

    // Settled, and already read for itself: nothing to ask. This is the pending push having just
    // landed, which re-runs the effect only to find there is no longer anything to wait for.

    let isActive = true;
    let timeoutId = null;
    pollCountRef.current = 0;
    failedPollCountRef.current = 0;

    /**
     * A read that failed is worth repeating whatever the bill's state was — the answer is unknown, not
     * settled. A read that succeeded is worth repeating only while the push is `pending`: a failure and a
     * reconciliation both wait on a person, so asking again would never change either.
     */
    const isWorthAskingAgain = didRead => {
      var _store$appfolioPush$g;
      return pollCountRef.current < MAX_POLLS && failedPollCountRef.current < MAX_FAILED_POLLS && (!didRead || ((_store$appfolioPush$g = store.appfolioPush.getStatus(billId)) === null || _store$appfolioPush$g === void 0 ? void 0 : _store$appfolioPush$g.status) === APPFOLIO_PUSH_STATUS.pending);
    };

    /**
     * Chained rather than scheduled on a fixed interval: `run` cancels the request still in flight, so a
     * tick landing mid-pass would abort it. The gap is measured from the end of one read to the next.
     */
    const read = async () => {
      pollCountRef.current += 1;
      let didRead = false;
      try {
        const resp = await store.appfolioPush.readPushStatusesAsync.run({
          billIds: [billId]
        }, undefined, {
          silent: true
        });
        didRead = !!(resp !== null && resp !== void 0 && resp.isSuccessful);
      } catch (_unused) {
        // A read that never reached the service throws rather than resolving. It failed all the same, and
        // left uncaught it ended the reads with nothing said.
      }
      if (!isActive) return;
      failedPollCountRef.current = didRead ? 0 : failedPollCountRef.current + 1;
      if (failedPollCountRef.current === MAX_FAILED_POLLS) toast('warning', STOPPED_UPDATING);
      if (isWorthAskingAgain(didRead)) timeoutId = setTimeout(read, POLL_INTERVAL_MS);
    };
    read();
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, billId, isOnItsWay]);
  return {
    pushStatus,
    getState: function (billExtractStatus) {
      var _listApPush$enrolled;
      let invoiceNumber = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
      return getAppfolioPushRowState({
        billExtractStatus,
        hasReadStatus: hasReadStatus || !!rowStatus,
        invoiceNumber,
        // Only the list says whether the bill is enrolled (LB-6031); the status read does not.
        isEnrolled: (_listApPush$enrolled = listApPush === null || listApPush === void 0 ? void 0 : listApPush.enrolled) !== null && _listApPush$enrolled !== void 0 ? _listApPush$enrolled : null,
        isPushing: store.appfolioPush.pushBillAsync.inProgress,
        needsPlan,
        apPushOnSetups,
        pushStatus
      });
    }
  };
};